import { db, collections, ROLES } from '../firebase.marketplace';
import { doc, getDoc, updateDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';

/**
 * Utility functions for admin operations
 */

/**
 * Make a user a super admin by email
 * @param {string} email - The email of the user to make admin
 * @returns {Promise<Object>} - The updated user data
 */
export const makeUserSuperAdmin = async (email) => {
  try {
    // Find the user by email
    const usersRef = collection(db, collections.USERS);
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error(`No user found with email: ${email}`);
    }
    
    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();
    const userRef = doc(db, collections.USERS, userDoc.id);
    
    // Update the user's roles
    const updatedRoles = {
      ...(userData.roles || {}),
      [ROLES.USER]: true,
      [ROLES.ADMIN]: true,
      [ROLES.ARTIST]: true,
      'superadmin': true
    };
    
    await updateDoc(userRef, {
      roles: updatedRoles,
      updatedAt: new Date()
    });
    
    // Get the updated user data
    const updatedUserDoc = await getDoc(userRef);
    
    return {
      id: updatedUserDoc.id,
      ...updatedUserDoc.data()
    };
  } catch (error) {
    console.error('Error making user super admin:', error);
    throw error;
  }
};

/**
 * Check if a user is a super admin
 * @param {string} uid - The user ID
 * @returns {Promise<boolean>} - Whether the user is a super admin
 */
export const isSuperAdmin = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, collections.USERS, uid));
    
    if (!userDoc.exists()) {
      return false;
    }
    
    const userData = userDoc.data();
    return userData.roles && userData.roles.superadmin === true;
  } catch (error) {
    console.error('Error checking if user is super admin:', error);
    return false;
  }
};

/**
 * List all users with admin role
 * @returns {Promise<Array>} - List of admin users
 */
export const listAdminUsers = async () => {
  try {
    const usersRef = collection(db, collections.USERS);
    const querySnapshot = await getDocs(usersRef);
    
    const adminUsers = [];
    
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      if (userData.roles && (userData.roles[ROLES.ADMIN] || userData.roles.superadmin)) {
        adminUsers.push({
          id: doc.id,
          ...userData
        });
      }
    });
    
    return adminUsers;
  } catch (error) {
    console.error('Error listing admin users:', error);
    throw error;
  }
};
